import { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { PageLoader } from "@/components/ui/page-loader";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { usePublicCycle } from "@/context/PublicCycleContext";
import {
  peekPublicCycleLink,
  verifyPublicCyclePassword,
  joinPublicCycleGuest,
  isTerminalPublicLinkStatus,
  type PublicCycleLinkStatus,
} from "@/lib/api/publicCycle";
import type { CompletedMatchContext } from "@/lib/api/rsvp";
import { useEnhancedToast } from "@/hooks/use-enhanced-toast";
import { usePageTitle } from "@/hooks/usePageTitle";
import { PublicCycleStatusScreen } from "@/components/v2/public/PublicCycleStatusScreen";

export function PublicCycleGate() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { success, error } = useEnhancedToast();
  const { session, setSession } = usePublicCycle();

  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [authenticated, setAuthenticated] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [loading, setLoading] = useState(true);
  const [requiresPassword, setRequiresPassword] = useState(true);
  const [groupId, setGroupId] = useState("");
  const [groupName, setGroupName] = useState("");
  const [players, setPlayers] = useState<{ id: number; name: string }[]>([]);
  const [votedPlayerIds, setVotedPlayerIds] = useState<Set<number>>(new Set());
  const [selectedPlayer, setSelectedPlayer] = useState("");
  const [guestName, setGuestName] = useState("");
  const [joiningGuest, setJoiningGuest] = useState(false);
  const [linkStatus, setLinkStatus] = useState<PublicCycleLinkStatus>("active");
  const [lastCompleted, setLastCompleted] = useState<CompletedMatchContext | null>(null);

  usePageTitle(t("v2.home.shareCycle", "Join"));

  useEffect(() => {
    const load = async () => {
      if (!token) return;
      setLoading(true);
      try {
        const peek = await peekPublicCycleLink(token);
        setGroupName(peek.group.name);
        setLinkStatus(peek.linkStatus);
        setLastCompleted(peek.lastCompleted);
        setRequiresPassword(peek.requiresPassword);
      } catch {
        setRequiresPassword(true);
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [token]);

  if (!token) {
    return <Navigate to="/" replace />;
  }

  if (session) {
    return <Navigate to={`/join/public/${token}/home`} replace />;
  }

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !password.trim()) return;

    setPasswordError(null);
    setVerifying(true);
    try {
      const snapshot = await verifyPublicCyclePassword(token, password.trim());
      setGroupId(snapshot.group.id);
      setGroupName(snapshot.group.name);
      setPlayers(snapshot.players);
      setVotedPlayerIds(new Set(snapshot.votedPlayerIds));
      setLinkStatus(snapshot.linkStatus);
      setLastCompleted(snapshot.lastCompleted);
      setAuthenticated(true);
    } catch (err) {
      const code = err instanceof Error ? err.message : "PUBLIC_CYCLE_FAILED";
      const message =
        code === "INVALID_PASSWORD"
          ? t("rsvp.invalidPassword")
          : code === "PASSWORD_NOT_SET"
            ? t("rsvp.linkNotReady")
            : code === "CYCLE_NOT_FOUND"
              ? t("v2.home.shareUnavailableDesc")
              : t("rsvp.verifyError", "Could not verify password");
      setPasswordError(message);
      error({ title: t("errors.error"), description: message });
    } finally {
      setVerifying(false);
    }
  };

  const handleJoinGuest = async () => {
    if (!token || !password.trim() || !guestName.trim()) return;
    setJoiningGuest(true);
    try {
      const name = guestName.trim();
      const playerId = await joinPublicCycleGuest({
        token,
        password: password.trim(),
        guestName: name,
      });
      setPlayers((prev) => [...prev, { id: playerId, name }]);
      setSelectedPlayer(String(playerId));
      setGuestName("");
      success({
        title: t("v2.home.guestJoined"),
        description: t("v2.home.guestJoinedDesc", { name }),
      });
    } catch {
      error({ title: t("errors.error"), description: t("v2.home.guestJoinError") });
    } finally {
      setJoiningGuest(false);
    }
  };

  const handleContinue = () => {
    if (!token || !password.trim() || !selectedPlayer) return;
    const player = players.find((p) => String(p.id) === selectedPlayer);
    if (!player) return;

    setSession({
      token,
      password: password.trim(),
      playerId: player.id,
      playerName: player.name,
      groupId,
      groupName,
    });
    navigate(`/join/public/${token}/home`, { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background relative">
        <PageLoader label={t("common.loading")} />
      </div>
    );
  }

  if (isTerminalPublicLinkStatus(linkStatus)) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <PublicCycleStatusScreen
          status={linkStatus}
          groupName={groupName}
          lastCompleted={lastCompleted}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background relative">
      <Card className="w-full max-w-lg relative z-10">
        <CardHeader>
          <CardTitle>{groupName || t("v2.home.pollTitle")}</CardTitle>
          <CardDescription>{t("v2.home.publicCycleGateDesc")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!requiresPassword ? (
            <p className="text-sm text-muted-foreground">{t("v2.home.publicPollLinkNotReady")}</p>
          ) : !authenticated ? (
            <form onSubmit={handlePasswordSubmit} className="space-y-3">
              <p className="text-sm text-muted-foreground">{t("rsvp.enterPassword")}</p>
              <Input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (passwordError) setPasswordError(null);
                }}
                placeholder={t("rsvp.passwordPlaceholder")}
                className="min-h-[44px]"
                required
              />
              {passwordError ? (
                <p className="text-sm text-destructive" role="alert">
                  {passwordError}
                </p>
              ) : null}
              <Button
                type="submit"
                className="w-full min-h-[44px] bg-soccer-primary text-black"
                disabled={verifying || !password.trim()}
              >
                {verifying ? t("common.loading") : t("rsvp.unlock")}
              </Button>
            </form>
          ) : (
            <>
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">{t("rsvp.selectName")}</p>
                <Select value={selectedPlayer} onValueChange={setSelectedPlayer}>
                  <SelectTrigger className="min-h-[44px]">
                    <SelectValue placeholder={t("rsvp.selectName")} />
                  </SelectTrigger>
                  <SelectContent>
                    {players
                      .filter((p) => !votedPlayerIds.has(p.id))
                      .map((p) => (
                        <SelectItem key={p.id} value={String(p.id)}>
                          {p.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <div className="flex gap-2">
                  <Input
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                    placeholder={t("v2.home.guestNamePlaceholder")}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    disabled={joiningGuest || !guestName.trim()}
                    onClick={handleJoinGuest}
                  >
                    {t("v2.home.joinAsGuest")}
                  </Button>
                </div>
              </div>
              <Button
                type="button"
                className="w-full min-h-[44px] bg-soccer-primary text-black"
                disabled={!selectedPlayer}
                onClick={handleContinue}
              >
                {t("v2.home.publicCycleContinue")}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
